import { ECommentStatus } from '@/features/view-tfl-uploaded/types'

/**
 * Routes Configuration
 *
 * Centralized route management with type safety and helper functions.
 * Best practices:
 * - All routes defined in one place for easy maintenance
 * - Type-safe route paths and parameters
 * - Route builder functions for dynamic routes
 * - Route groups for better organization
 */

// ============================================================================
// Route Paths
// ============================================================================

/**
 * Base route paths
 * Use these constants instead of hardcoding paths throughout the app
 */
export const ROUTES = {
  // Public routes
  ROOT: '/',
  LOGIN: '/login',
  SIGN_UP: '/sign-up',
  UNAUTHORIZED: '/unauthorized',
  INVITE: '/invite',
  NOT_FOUND: '/404',
  INTERNAL_SERVER_ERROR: '/500',

  // Protected routes
  DASHBOARD: '/dashboard',
  MY_WORK: '/my-work',
  COMPANY: '/company',
  PROJECT: '/project',
  STUDY: '/study',
  REPORTING_EVENT: '/reporting-event',
  DELIVERABLE_SET: '/deliverable-set',
  COMMENT: '/comment',
  TEAMWORK_INTEGRATIONS: '/teamwork-integrations',
  USER_MAPPING: '/user-mapping',
  USER_MANAGEMENT: '/user-management',
  FILE_MANAGER: '/file-manager',
  FILE_UPLOAD_BASE: '/deliverable-set/upload',
  FILE_UPLOAD_FROM_DETAIL: '/deliverable-set/:id/upload',
  FILE_UPLOAD_FROM_DOCUMENT: '/deliverable-set/:id/document/upload',
  TFL_VIEWER_BASE: '/deliverable-set/:id/tfl',
  NOTIFICATION_SETTINGS: '/settings/notifications',
  COMMENT_TEMPLATES: '/settings/comment-templates',
  WORKFLOW_CONFIG: '/workflow-config',
  HISTORY: '/history',
} as const

// ============================================================================
// Type Safety
// ============================================================================

/**
 * Type for route keys - ensures type safety when referencing routes
 */
export type RouteKey = keyof typeof ROUTES

/**
 * Type for route values - ensures route paths are strings
 */
export type RoutePath = (typeof ROUTES)[RouteKey]

// ============================================================================
// Route Groups
// ============================================================================

/**
 * Route groups for better organization
 * Useful for navigation, breadcrumbs, and route-based features
 */
export const ROUTE_GROUPS = {
  PUBLIC: [ROUTES.LOGIN, ROUTES.INVITE, ROUTES.UNAUTHORIZED, ROUTES.NOT_FOUND],
  ANALYTICS: [ROUTES.DASHBOARD],
  TFL_REVIEW: [
    ROUTES.COMPANY,
    ROUTES.PROJECT,
    ROUTES.STUDY,
    ROUTES.REPORTING_EVENT,
    ROUTES.DELIVERABLE_SET,
    ROUTES.FILE_MANAGER,
    ROUTES.FILE_UPLOAD_BASE,
    ROUTES.FILE_UPLOAD_FROM_DETAIL,
    ROUTES.FILE_UPLOAD_FROM_DOCUMENT,
    ROUTES.TFL_VIEWER_BASE,
  ],
  PROTECTED: [
    ROUTES.DASHBOARD,
    ROUTES.MY_WORK,
    ROUTES.COMPANY,
    ROUTES.PROJECT,
    ROUTES.STUDY,
    ROUTES.REPORTING_EVENT,
    ROUTES.DELIVERABLE_SET,
    ROUTES.USER_MAPPING,
    ROUTES.USER_MANAGEMENT,
    ROUTES.FILE_MANAGER,
    ROUTES.FILE_UPLOAD_BASE,
    ROUTES.FILE_UPLOAD_FROM_DETAIL,
    ROUTES.FILE_UPLOAD_FROM_DOCUMENT,
    ROUTES.TFL_VIEWER_BASE,
    ROUTES.NOTIFICATION_SETTINGS,
    ROUTES.COMMENT_TEMPLATES,
    ROUTES.WORKFLOW_CONFIG,
    ROUTES.HISTORY,
  ],
} as const

/**
 * Routes that should not have padding in MainLayout
 * Used to provide full-width layouts for specific pages
 */
export const NO_PADDING_ROUTES = [ROUTES.WORKFLOW_CONFIG] as const

// ============================================================================
// Route Builders
// ============================================================================

/**
 * Route builder functions for dynamic routes with parameters
 * Example: buildProjectRoute(123) => '/project/123'
 */

/**
 * Build a company detail route with ID parameter
 * @param id - Company ID
 * @returns Route path with ID
 */
export const buildCompanyRoute = (id: string | number): string => {
  return `${ROUTES.COMPANY}/${id}`
}

/**
 * Build a project detail route with ID parameter
 * @param id - Project ID
 * @returns Route path with ID
 */
export const buildProjectRoute = (id: string | number): string => {
  return `${ROUTES.PROJECT}/${id}`
}

/**
 * Build a deliverable set detail route with ID parameter
 * @param id - Deliverable Set ID
 * @returns Route path with ID
 */
export const buildDeliverableSetRoute = (id: string | number): string => {
  return `${ROUTES.DELIVERABLE_SET}/${id}`
}

/**
 * Build a deliverable set document route (TFL files list)
 * @param id - Deliverable Set ID
 * @returns Route path for deliverable set documents (TFL files)
 */
export const buildDeliverableSetDocument = (id: string | number): string => {
  return `${buildDeliverableSetRoute(id)}/document`
}

/**
 * Build file upload route from deliverable set list page
 * @returns Route path for file upload (with dropdown selection)
 */
export const buildFileUploadRoute = (): string => {
  return ROUTES.FILE_UPLOAD_BASE
}

/**
 * Build file upload route from deliverable set detail page
 * @param id - Deliverable Set ID
 * @returns Route path for file upload (pre-populated deliverable set)
 */
export const buildFileUploadFromDetailRoute = (id: string | number): string => {
  return `/deliverable-set/${id}/upload`
}

/**
 * Build file upload route from document page
 * @param id - Deliverable Set ID
 * @returns Route path for file upload from document page (pre-populated deliverable set)
 */
export const buildFileUploadFromDocumentRoute = (
  id: string | number
): string => {
  return `/deliverable-set/${id}/document/upload`
}

// ============================================================================
// Route Helpers
// ============================================================================

/**
 * Check if a route is a public route
 * @param pathname - Current pathname
 * @returns True if route is public
 */
export const isPublicRoute = (pathname: string): boolean => {
  return (ROUTE_GROUPS.PUBLIC as readonly string[]).includes(pathname)
}

/**
 * Check if a route is a protected route
 * @param pathname - Current pathname
 * @returns True if route is protected
 */
export const isProtectedRoute = (pathname: string): boolean => {
  return (ROUTE_GROUPS.PROTECTED as readonly string[]).includes(pathname)
}

/**
 * Get nested route path (without leading slash)
 * Useful for React Router v6 nested routes
 * Example: getNestedRoutePath(ROUTES.COMPANY) => 'company'
 * @param route - Route path
 * @returns Route path without leading slash
 */
export const getNestedRoutePath = (route: RoutePath): string => {
  return route.startsWith('/') ? route.slice(1) : route
}

/**
 * Validate if a pathname matches a route pattern
 * @param pathname - Pathname to validate
 * @param route - Route path to match against
 * @returns True if pathname matches route
 */
export const matchesRoute = (pathname: string, route: RoutePath): boolean => {
  // Exact match
  if (pathname === route) return true

  // Dynamic route match (e.g., '/project/123' matches '/project')
  const routePattern = new RegExp(`^${route}(?:/|$)`)
  return routePattern.test(pathname)
}

// ============================================================================
// Comment Page Route Helpers
// ============================================================================

/**
 * Comment filters type for location state
 */
export interface CommentNavigationState {
  filters?: {
    deliverableSetId?: string | null
    tflFileId?: string | null
    status?: ECommentStatus.OPEN | ECommentStatus.RESOLVED | null
    reviewStatus?: string | null
    action?: string | null
    projectId?: string | null
  }
}

/**
 * Build comment list page navigation path
 * Always returns an object with path and optional state
 * @param filters - Optional filters for comment list
 * @returns Object with path and state (state may be undefined if no filters)
 *
 * @example
 * const { path, state } = buildCommentRoute()
 * navigate(path, { state })
 *
 * @example
 * const { path, state } = buildCommentRoute({ status: ECommentStatus.OPEN })
 * navigate(path, { state })
 *
 * @example
 * const { path, state } = buildCommentRoute({ projectId: 'project-123' })
 * navigate(path, { state })
 */
export const buildCommentRoute = (filters?: {
  deliverableSetId?: string | null
  tflFileId?: string | null
  status?: ECommentStatus.OPEN | ECommentStatus.RESOLVED | null
  reviewStatus?: string | null
  action?: string | null
  projectId?: string | null
}): { path: string; state?: CommentNavigationState } => {
  if (!filters) {
    return { path: ROUTES.COMMENT }
  }

  const cleanFilters: CommentNavigationState['filters'] = {}

  if (filters.deliverableSetId) {
    cleanFilters.deliverableSetId = filters.deliverableSetId
  }
  if (filters.tflFileId) {
    cleanFilters.tflFileId = filters.tflFileId
  }
  if (filters.status) {
    cleanFilters.status = filters.status
  }
  if (filters.reviewStatus) {
    cleanFilters.reviewStatus = filters.reviewStatus
  }
  if (filters.action) {
    cleanFilters.action = filters.action
  }
  if (filters.projectId) {
    cleanFilters.projectId = filters.projectId
  }

  if (Object.keys(cleanFilters).length === 0) {
    return { path: ROUTES.COMMENT }
  }

  return {
    path: ROUTES.COMMENT,
    state: { filters: cleanFilters },
  }
}
